/**
 * Global motion + cart helpers (loaded with defer on every page).
 * - Scroll reveal: [data-reveal] gets .is-revealed when it enters the viewport.
 * - Pointer tilt: [data-tilt="6"] leans toward the cursor (fine pointers only).
 * - Scroll parallax: [data-parallax="0.08"] drifts at a fraction of scroll speed.
 * - Reveal stagger: children of [data-reveal-stagger] get data-reveal + delays.
 * - Image settle: img.media-fade / card images get .is-loaded once decoded.
 * - Cart count: `cart:updated` events refresh every [data-cart-count].
 * - <free-shipping-bar>: item-based bundle offer progress (20% off + free shipping at 2+ items).
 * - <cart-toast>: mini "added to cart" feedback on `cart:item-added`.
 * - Count up: [data-count-up] numbers ("2,400", "4.9") roll up when first seen.
 * - Heart burst: a few hearts float up from the add-to-cart button on `cart:item-added`.
 * - Cart API: theme.cartAdd(payload), theme.fetchCart(), theme.announceAdd(result).
 * Public API: window.theme.initMotion(scope), window.theme.formatMoney(cents).
 * All motion is skipped with prefers-reduced-motion or the theme motion setting off.
 */
(() => {
  const root = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOff = () =>
    reduceQuery.matches || getComputedStyle(root).getPropertyValue('--motion-scale').trim() === '0';

  /* ---------- Reveal ---------- */
  const REVEAL = '[data-reveal]:not(.is-revealed)';
  const revealAll = (scope = document) =>
    scope.querySelectorAll(REVEAL).forEach((el) => el.classList.add('is-revealed'));

  let revealObserver = null;
  if ('IntersectionObserver' in window && !motionOff()) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.1 }
    );
  }
  const applyStagger = (scope = document) => {
    scope.querySelectorAll('[data-reveal-stagger]').forEach((parent) => {
      const step = parseInt(parent.dataset.revealStagger, 10) || 4;
      Array.from(parent.children).forEach((child, index) => {
        if (child.hasAttribute('data-reveal')) return;
        child.setAttribute('data-reveal', '');
        child.style.setProperty('--reveal-delay', index % step);
      });
    });
  };

  const observeReveal = (scope = document) => {
    applyStagger(scope);
    if (!revealObserver) return revealAll(scope);
    scope.querySelectorAll(REVEAL).forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Tilt ---------- */
  const bindTilt = (scope = document) => {
    if (!fineQuery.matches || motionOff()) return;
    scope.querySelectorAll('[data-tilt]:not([data-tilt-bound])').forEach((el) => {
      el.setAttribute('data-tilt-bound', '');
      const max = parseFloat(el.dataset.tilt) || 6;
      const target = el.closest('[data-tilt-area]') || el;
      let frame = 0;

      target.addEventListener('pointermove', (event) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = target.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          el.classList.add('is-tilting');
          el.style.setProperty('--tilt-y', `${(x * max).toFixed(2)}deg`);
          el.style.setProperty('--tilt-x', `${(-y * max).toFixed(2)}deg`);
        });
      });
      target.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        el.classList.remove('is-tilting');
        el.style.setProperty('--tilt-x', '0deg');
        el.style.setProperty('--tilt-y', '0deg');
      });
    });
  };

  /* ---------- Parallax ---------- */
  const parallaxItems = new Set();
  let parallaxTicking = false;
  const updateParallax = () => {
    const vh = window.innerHeight;
    parallaxItems.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const offset = rect.top + rect.height / 2 - vh / 2;
      const factor = parseFloat(el.dataset.parallax) || 0.06;
      el.style.setProperty('--parallax-y', `${(-offset * factor).toFixed(1)}px`);
    });
    parallaxTicking = false;
  };
  const requestParallax = () => {
    if (parallaxTicking || parallaxItems.size === 0) return;
    parallaxTicking = true;
    requestAnimationFrame(updateParallax);
  };
  let parallaxObserver = null;
  const bindParallax = (scope = document) => {
    if (motionOff() || !('IntersectionObserver' in window)) return;
    parallaxObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) parallaxItems.add(entry.target);
        else parallaxItems.delete(entry.target);
      });
      requestParallax();
    });
    scope.querySelectorAll('[data-parallax]').forEach((el) => parallaxObserver.observe(el));
  };
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });

  /* ---------- Cart count ---------- */
  document.addEventListener('cart:updated', (event) => {
    const count = event.detail && typeof event.detail.item_count === 'number' ? event.detail.item_count : null;
    if (count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      // Badges are text-only. Never overwrite an element that holds markup
      // (e.g. a price wrapper or component that reused the attribute).
      if (badge.childElementCount > 0 || badge.matches('[data-price-live], [data-price], cross-sell-bundle')) return;
      badge.textContent = count;
      badge.hidden = count === 0;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
    });
    document.querySelectorAll('[data-cart-label]').forEach((label) => {
      const template = count === 1 ? label.dataset.labelOne : label.dataset.labelOther;
      if (template) label.textContent = template.replace('[count]', count);
    });
  });

  /* ---------- Image settle ---------- */
  const IMG = 'img.media-fade, .product-card__media > img';
  const markLoaded = (event) => {
    const img = event.target;
    if (img instanceof HTMLImageElement && img.matches(IMG)) img.classList.add('is-loaded');
  };
  document.addEventListener('load', markLoaded, true);
  document.addEventListener('error', markLoaded, true);
  const settleImages = (scope = document) =>
    scope.querySelectorAll(IMG).forEach((img) => {
      if (img.complete) img.classList.add('is-loaded');
    });

  /* ---------- Money ---------- */
  const moneyFormat = () => {
    const holder = document.querySelector('[data-money-format]');
    return (holder && holder.dataset.moneyFormat) || '{{amount}}';
  };
  const formatMoney = (cents, format = moneyFormat()) => {
    const value = Number(cents) || 0;
    const group = (decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(0, ',', '.');
        case 'amount_with_comma_separator':
          return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(0, '.', ',');
        case 'amount_with_space_separator':
          return group(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(0, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return group(2, "'", '.');
        default:
          return group(2, ',', '.');
      }
    });
  };

  /* ---------- Count up ---------- */
  const parseCount = (text) => {
    const match = text.match(/^(\D*?)(\d[\d.,\s]*\d|\d)(\D*)$/);
    if (!match) return null;
    const [, prefix, raw, suffix] = match;
    const decimalMatch = raw.match(/[.,](\d{1,2})$/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    const decimalChar = decimalMatch ? raw.charAt(raw.length - decimals - 1) : '';
    const body = decimalMatch ? raw.slice(0, raw.length - decimals - 1) : raw;
    const groupMatch = body.match(/\D/);
    const group = groupMatch ? groupMatch[0] : '';
    const value = parseFloat(body.replace(/\D/g, '') + '.' + (decimalMatch ? decimalMatch[1] : '0'));
    if (!Number.isFinite(value)) return null;
    const format = (n) => {
      const [whole, fraction] = n.toFixed(decimals).split('.');
      const grouped = group ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, group) : whole;
      return prefix + grouped + (fraction ? decimalChar + fraction : '') + suffix;
    };
    return { value, format };
  };

  let countObserver = null;
  const runCount = (el) => {
    const parsed = parseCount(el.dataset.countFinal || '');
    if (!parsed) return;
    const duration = 1400;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = t < 1 ? parsed.format(parsed.value * eased) : el.dataset.countFinal;
      if (t < 1) requestAnimationFrame(step);
    };
    el.textContent = parsed.format(0);
    requestAnimationFrame(step);
  };
  const bindCounters = (scope = document) => {
    if (motionOff() || !('IntersectionObserver' in window)) return;
    countObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          countObserver.unobserve(entry.target);
          runCount(entry.target);
        });
      },
      { threshold: 0.6 }
    );
    scope.querySelectorAll('[data-count-up]:not([data-count-final])').forEach((el) => {
      const text = el.textContent.trim();
      if (!parseCount(text)) return;
      el.dataset.countFinal = text;
      countObserver.observe(el);
    });
  };

  /* ---------- Smoke-puff burst on add to cart ---------- */
  const HEART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle fill="currentColor" cx="12" cy="12" r="10"/></svg>';
  const heartBurst = (source) => {
    if (motionOff() || !(source instanceof Element) || !source.isConnected) return;
    const rect = source.getBoundingClientRect();
    if (!rect.width) return;
    const burst = document.createElement('div');
    burst.className = 'heart-burst';
    burst.setAttribute('aria-hidden', 'true');
    burst.style.left = rect.left + rect.width / 2 + 'px';
    burst.style.top = rect.top + rect.height / 2 + 'px';
    for (let i = 0; i < 7; i += 1) {
      const heart = document.createElement('span');
      heart.className = 'heart-burst__heart';
      const angle = (-90 + (i - 3) * 22 + (Math.random() * 10 - 5)) * (Math.PI / 180);
      const distance = 56 + Math.random() * 44;
      heart.style.setProperty('--dx', (Math.cos(angle) * distance).toFixed(1) + 'px');
      heart.style.setProperty('--dy', (Math.sin(angle) * distance).toFixed(1) + 'px');
      heart.style.setProperty('--rot', (Math.random() * 40 - 20).toFixed(0) + 'deg');
      heart.style.setProperty('--size', 10 + Math.round(Math.random() * 8) + 'px');
      heart.style.setProperty('--delay', i * 35 + 'ms');
      heart.innerHTML = HEART;
      burst.append(heart);
    }
    document.body.append(burst);
    setTimeout(() => burst.remove(), 1700);
  };
  document.addEventListener('cart:item-added', (event) => {
    heartBurst(event.detail && event.detail.source);
  });

  /* ---------- Init ---------- */
  const init = (scope = document) => {
    observeReveal(scope);
    bindTilt(scope);
    bindParallax(scope);
    bindCounters(scope);
    settleImages(scope);
  };
  init();

  window.theme = Object.assign(window.theme || {}, { initMotion: init, formatMoney, motionOff });

  document.addEventListener('shopify:section:load', (event) => {
    if (window.Shopify && window.Shopify.designMode) revealAll(event.target);
    init(event.target);
  });

  /* ---------- Cart API (shared by every add-to-cart in the theme) ----------
     cartAdd(payload): POST /cart/add.js (FormData or { items }) and asks for
     the cart drawer section in the same request, then reads a fresh /cart.js
     (no-store + cache-buster) as the source of truth.
     announceAdd(result): dispatches `cart:updated` (cart) and
     `cart:item-added` ({ item, cart, sections, source }). */
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const fetchCart = async () => {
    const response = await fetch(`${routesRoot}cart.js?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  };
  const cartSectionIds = () => {
    const drawer = document.querySelector('cart-drawer:not([data-disabled])');
    return drawer && drawer.dataset.sectionId ? [drawer.dataset.sectionId] : [];
  };
  const cartAdd = async (payload) => {
    const isForm = payload instanceof FormData;
    const sections = cartSectionIds();
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    let body = payload;
    if (isForm) {
      if (sections.length) {
        body.set('sections', sections.join(','));
        body.set('sections_url', window.location.pathname);
      }
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(sections.length ? { ...payload, sections, sections_url: window.location.pathname } : payload);
    }
    const response = await fetch(`${routesRoot}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers, body });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || '');
      error.status = response.status;
      throw error;
    }
    let cart = null;
    for (let attempt = 0; attempt < 2 && !cart; attempt += 1) {
      try {
        cart = await fetchCart();
      } catch (error) {
        cart = null;
      }
    }
    // Fires once per successful /cart/add.js (analytics hook, e.g. meta-pixel.js → AddToCart).
    try {
      document.dispatchEvent(
        new CustomEvent('cart:add-success', { detail: { items: data.items || [data], payload, cart } })
      );
    } catch (error) {
      /* never block the add flow */
    }
    return { data, item: data.items ? data.items[0] : data, cart, sections: data.sections || null };
  };
  const announceAdd = ({ item, cart, sections, source }) => {
    if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item, cart, sections, source } }));
  };
  window.theme = Object.assign(window.theme || {}, { cartAdd, fetchCart, announceAdd });

  /* ---------- <free-shipping-bar> ----------
     Item-based bundle offer progress: total cart quantity vs data-min
     (shop metafield cuddle.bundle_discount). Copy comes pre-translated in
     data-text-* (start / away-one / away-other with [n] / unlocked). */
  let lastShippingRatio = null;
  if (!customElements.get('free-shipping-bar')) {
    customElements.define(
      'free-shipping-bar',
      class FreeShippingBar extends HTMLElement {
        connectedCallback() {
          this.fill = this.querySelector('.progress-track__fill');
          this.track = this.querySelector('[role="progressbar"]');
          this.message = this.querySelector('[data-message]');
          this.min = Math.max(1, Number(this.dataset.min) || 2);
          this.onCart = (event) => {
            const cart = event.detail || {};
            if (typeof cart.item_count === 'number') this.update(cart.item_count);
          };
          document.addEventListener('cart:updated', this.onCart);

          const count = Number(this.dataset.count) || 0;
          const previous = lastShippingRatio;
          if (motionOff() || !('IntersectionObserver' in window)) return this.update(count);
          // Grow the first time the bar is seen: from empty, or from the last
          // shown value when the bar was re-rendered (cart drawer / cart page).
          this.update(count, { progress: previous === null ? 0 : previous });
          this.observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            requestAnimationFrame(() => requestAnimationFrame(() => this.update(this.count, { from: previous })));
          });
          this.observer.observe(this);
        }

        disconnectedCallback() {
          document.removeEventListener('cart:updated', this.onCart);
          if (this.observer) this.observer.disconnect();
          if (this.celebrateObserver) this.celebrateObserver.disconnect();
          clearTimeout(this.celebrateTimer);
        }

        text(count) {
          const remaining = Math.max(this.min - count, 0);
          if (count <= 0) return this.dataset.textStart || '';
          if (remaining === 0) return this.dataset.textUnlocked || '';
          const template = remaining === 1 ? this.dataset.textAwayOne : this.dataset.textAwayOther;
          return (template || '').split('[n]').join(String(remaining));
        }

        update(count, { progress, from } = {}) {
          const before = typeof from === 'number' ? from : this.ratio;
          this.count = count;
          const real = Math.min(Math.max(count, 0) / this.min, 1);
          const ratio = typeof progress === 'number' ? progress : real;
          if (typeof progress !== 'number') {
            lastShippingRatio = real;
            this.ratio = real;
          }
          if (this.fill) this.fill.style.setProperty('--progress', ratio.toFixed(4));
          if (this.track) this.track.setAttribute('aria-valuenow', String(Math.round(real * 100)));
          const message = this.text(count);
          if (this.message && message && this.message.textContent !== message) this.message.textContent = message;
          this.classList.toggle('is-unlocked', real >= 1);
          // Small celebration only on the transition locked -> unlocked.
          if (typeof progress !== 'number' && real >= 1 && typeof before === 'number' && before < 1) this.celebrate();
        }

        celebrate() {
          if (motionOff()) return;
          // Hidden right now (closed drawer / toast): play once it is shown.
          if (!this.getClientRects().length && 'IntersectionObserver' in window) {
            if (this.celebrateObserver) this.celebrateObserver.disconnect();
            this.celebrateObserver = new IntersectionObserver((entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.celebrateObserver.disconnect();
              this.celebrateObserver = null;
              if (this.classList.contains('is-unlocked')) setTimeout(() => this.celebrate(), 200);
            });
            this.celebrateObserver.observe(this);
            return;
          }
          this.classList.remove('is-celebrating');
          void this.offsetWidth;
          this.classList.add('is-celebrating');
          clearTimeout(this.celebrateTimer);
          this.celebrateTimer = setTimeout(() => this.classList.remove('is-celebrating'), 1600);
        }
      }
    );
  }

  /* ---------- <cart-toast> ---------- */
  if (!customElements.get('cart-toast')) {
    customElements.define(
      'cart-toast',
      class CartToast extends HTMLElement {
        connectedCallback() {
          const close = this.querySelector('[data-toast-close]');
          if (close) close.addEventListener('click', () => this.hide());
          this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.hide();
          });
          // Pause auto-dismiss while the shopper is interacting with it.
          ['pointerenter', 'focusin'].forEach((type) => this.addEventListener(type, () => clearTimeout(this.timer)));
          ['pointerleave', 'focusout'].forEach((type) =>
            this.addEventListener(type, () => {
              requestAnimationFrame(() => {
                if (!this.contains(document.activeElement) && !this.matches(':hover')) this.schedule();
              });
            })
          );
          this.onAdded = (event) => this.show(event.detail || {});
          document.addEventListener('cart:item-added', this.onAdded);
        }

        disconnectedCallback() {
          document.removeEventListener('cart:item-added', this.onAdded);
        }

        show({ item, cart, source }) {
          // The cart drawer (assets/cart-drawer.js) handles feedback when present.
          if (!item || root.hasAttribute('data-cart-drawer')) return;
          this.returnFocus = source || null;
          const set = (selector, value) => {
            const el = this.querySelector(selector);
            if (!el) return;
            el.textContent = value || '';
            el.hidden = !value;
          };
          set('[data-toast-title]', item.product_title);
          const variant = item.variant_title && !/default title/i.test(item.variant_title) ? item.variant_title : '';
          set('[data-toast-variant]', variant);
          set('[data-toast-price]', formatMoney(item.final_line_price != null ? item.final_line_price : item.price));

          const media = this.querySelector('[data-toast-image]');
          if (media) {
            media.replaceChildren();
            const src = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
            if (src) {
              const img = new Image(240, 160);
              const url = new URL(src, location.href);
              url.searchParams.set('width', '240');
              img.src = url.toString();
              img.alt = '';
              media.append(img);
            }
            media.hidden = !src;
          }

          if (cart) {
            this.querySelectorAll('[data-toast-count]').forEach((el) => {
              el.textContent = cart.item_count;
            });
          }
          const live = document.querySelector('[data-toast-live]');
          if (live) live.textContent = `${this.dataset.textAdded || ''} ${item.product_title}`.trim();

          this.classList.add('is-visible');
          this.schedule();
        }

        schedule() {
          clearTimeout(this.timer);
          if (!this.classList.contains('is-visible')) return;
          this.timer = setTimeout(() => this.hide(), 6000);
        }

        hide() {
          clearTimeout(this.timer);
          const hadFocus = this.contains(document.activeElement);
          this.classList.remove('is-visible');
          if (hadFocus && this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus();
        }
      }
    );
  }
})();
